import { Directive, Inject, Input, forwardRef, ElementRef, Renderer2, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { DOCUMENT } from '@angular/common';
import { equals, nth } from 'ramda';
import { splitPath, orS } from '@app/helpers/function.helpers';
import { Subscription } from 'rxjs';
import { TranslateService } from "@ngx-translate/core";
import { GlobalTranslateService } from '@app/services/globalTranslate/globalTranslate.service';
import { ServiceConfigService } from '@app/services/service-config.service';

@Directive({
    selector: 'select[nhTranslate]',
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SelectTranslateDirective), multi: true }],
    standalone: false
})
export class SelectTranslateDirective implements ControlValueAccessor, OnDestroy, OnInit {
  private _value: any;
  private _items: any[] = [];
  private _originalItems: any[] = [];
  private _fragment: DocumentFragment;
  private langSubscription: Subscription;
  private currentLang: string;
  private isTranslating: boolean = false;
  private pickOptionTranslated: boolean = false;
  private translationCache: Map<string, string> = new Map();

  @HostBinding('class') class = 'custom-select-info';
  @Input() name: string;
  @Input() readonly bindLabel: string = 'value';
  @Input() readonly bindValue: string = '';
  @Input() readonly empty: any | null = null;

  public onChange = (_: any) => {};
  public onTouched = () => {};
  public pickAnyOptionText: any;

  constructor(
    @Inject(DOCUMENT) private _document: HTMLDocument,
    private _renderer: Renderer2,
    private _element: ElementRef,
    public translateService: TranslateService,
    private globalTranslateService: GlobalTranslateService,
    private serverConfig: ServiceConfigService
  ) {
    // Get current language
    const storedLang = sessionStorage.getItem('globalTranslatedLan');
    this.currentLang = storedLang && storedLang !== 'undefined' ? storedLang : sessionStorage.getItem('currentLang') ? sessionStorage.getItem('currentLang') : 'en';
    // Initialize pick option text (async, but don't wait or recreate options)
    this.setPickOptionText();

    // Add change listener to handle user selection
    this._renderer.listen(this._element.nativeElement, 'change', (event) => {
      const selectedIndex = event.target.selectedIndex - 1; // -1 because of "Pick an option"
      if (selectedIndex >= 0 && selectedIndex < this._originalItems.length) {
        const selectedItem = this._originalItems[selectedIndex];
        const value = this.itemValue(selectedItem);
        this._value = value; // Update internal value
        this.onChange(value);
      } else {
        // User selected "Pick an option" - clear the value
        this._value = null;
        this.onChange(null);
      }
    });

    // Add blur listener for touched state
    this._renderer.listen(this._element.nativeElement, 'blur', () => {
      this.onTouched();
    });

    // Subscribe to language changes
    this.langSubscription = this.translateService.onDefaultLangChange.subscribe(async (resLang) => {
      let awstranslateSwitch = this.serverConfig.getServerConfig().awsTranslateSwitch;
      if (resLang?.lang && awstranslateSwitch) {
        const newLang = sessionStorage.getItem('globalTranslatedLan');
        if (newLang && newLang !== 'undefined' && newLang !== this.currentLang) {
          this.currentLang = newLang;
          this.pickOptionTranslated = false; // Reset flag for new language
          await this.setPickOptionText();
          // Re-translate items when language changes
          if (this._originalItems.length > 0) {
            this.translateAndCreateOptions(this._originalItems);
          }
        }
      }
    });
  }

  ngOnInit() {
    // Subscribe to language changes - done in ngOnInit to ensure this.currentLang is initialized
    this.langSubscription = this.translateService.onDefaultLangChange.subscribe(async (resLang) => {
      let awstranslateSwitch = this.serverConfig.getServerConfig().awsTranslateSwitch;
      if (resLang?.lang && !awstranslateSwitch) {
        // Use resLang.lang as the new language source
        const newLang = resLang.lang;
        
        if (newLang && newLang !== this.currentLang) {
          this.currentLang = newLang;
          this.translationCache.clear();
          this.pickOptionTranslated = false; // Reset flag for new language
          await this.setPickOptionText();
          // Re-translate items when language changes
          if (this._originalItems.length > 0) {
            this.translateAndCreateOptions(this._originalItems);
          }
        }
      }
    });
  }

  @Input()
  set items(items: any[]) {
    this._originalItems = items || [];
    this.translateAndCreateOptions(this._originalItems);
  }

  get items() {
    return this._items;
  }

  get value() {
    return this._value;
  }

  ngOnDestroy() {
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }

  private async translateAndCreateOptions(items: any[]) {
    // Prevent concurrent translations
    if (this.isTranslating) {
      return;
    }

    if (!items || items.length === 0) {
      this._items = [];
      this.createOptionsFromTranslated([]);
      return;
    }

    this.isTranslating = true;

    try {
      // Check if we need to translate (only translate if language is not 'en', 'es', or 'sp')
      const needsTranslation = this.currentLang && !['en', 'es', 'sp'].includes(this.currentLang);

      // Ensure "Pick an option" is translated before creating options
      if (needsTranslation && !this.pickOptionTranslated) {
        await this.setPickOptionText();
      }

      if (needsTranslation) {
        // Translate items using GlobalTranslateService with caching
        const translatedItems = await Promise.all(
          items.map(async (item) => {
            if (item && item.value) {
              // Check cache first
              const cacheKey = `${this.currentLang}_${item.value}`;
              if (this.translationCache.has(cacheKey)) {
                return {
                  ...item,
                  value: this.translationCache.get(cacheKey)
                };
              }

              // Translate and cache
              try {
                const translatedValue = await this.globalTranslateService.translate(item.value, this.currentLang);
                const finalValue = translatedValue || item.value;
                this.translationCache.set(cacheKey, finalValue);
                return {
                  ...item,
                  value: finalValue
                };
              } catch (error) {
                console.warn(`Translation failed for "${item.value}", using original`, error);
                this.translationCache.set(cacheKey, item.value);
                return item;
              }
            }
            return item;
          })
        );
        this._items = translatedItems;
      } else {
        // No translation needed, use original items
        this._items = [...items];
      }

      this.createOptionsFromTranslated(this._items);
    } finally {
      this.isTranslating = false;
    }
  }

  private createOptionsFromTranslated(items: any[]) {
    this._renderer.setProperty(this._element.nativeElement, 'textContent', null);
    this._fragment = this._document.createDocumentFragment();
    
    // Add "Pick an option" as first option
    this.createPickOption();
    
    // Add all other options with their index
    items.forEach((item, index) => {
      this.createOption(item, index);
    });
    
    this._renderer.appendChild(this._element.nativeElement, this._fragment);
    
    // Re-apply selection if there's a stored value (only once after DOM update)
    if (this._value) {
      // Use microtask to ensure DOM is updated
      Promise.resolve().then(() => {
        this.setSelected(this._value);
      });
    }
  }

  private createPickOption() {
    const option = this._renderer.createElement('option');
    this._renderer.setProperty(option, 'textContent', this.pickAnyOptionText);
    this._renderer.setProperty(option, 'value', '');
    this._renderer.appendChild(this._fragment, option);
  }

  private createOption(item: any, index: number) {
    const option = this._renderer.createElement('option');
    this._renderer.setProperty(option, 'textContent', this.itemLabel(item));
    // Store index as value to map back to original items
    this._renderer.setProperty(option, 'value', index.toString());
    this._renderer.appendChild(this._fragment, option);
  }

  private async setPickOptionText() {
    // Check if already translated for this language
    const cacheKey = `pick_option_${this.currentLang}`;
    if (this.translationCache.has(cacheKey)) {
      this.pickAnyOptionText = this.translationCache.get(cacheKey);
      this.pickOptionTranslated = true;
      return;
    }

    const needsTranslation = this.currentLang && !['en', 'es', 'sp'].includes(this.currentLang);
    
    // Check for Spanish language (both 'sp' and 'es')
    if (this.currentLang === "sp" || this.currentLang === "es") {
      this.pickAnyOptionText = 'Elija una opción';
      this.translationCache.set(cacheKey, this.pickAnyOptionText);
      this.pickOptionTranslated = true;
    } else if (needsTranslation) {
      // Translate "Pick an option" for other languages
      try {
        this.pickAnyOptionText = await this.globalTranslateService.translate('Pick an option', this.currentLang);
        this.translationCache.set(cacheKey, this.pickAnyOptionText);
        this.pickOptionTranslated = true;
      } catch (error) {
        console.warn('Translation failed for "Pick an option", using default', error);
        this.pickAnyOptionText = 'Pick an option';
        this.translationCache.set(cacheKey, this.pickAnyOptionText);
        this.pickOptionTranslated = true;
      }
    } else {
      this.pickAnyOptionText = 'Pick an option';
      this.translationCache.set(cacheKey, this.pickAnyOptionText);
      this.pickOptionTranslated = true;
    }
  }

  writeValue(value: any): void {
    // Normalize empty string and undefined to null
    const normalizedValue = (value === '' || value === undefined) ? null : value;
    
    // Only update if value actually changed
    if (equals(this._value, normalizedValue)) {
      return;
    }
    
    this._value = normalizedValue;
    
    // Use microtask instead of setTimeout for better performance
    Promise.resolve().then(() => {
      this.setSelected(this._value);
    });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._renderer.setProperty(this._element.nativeElement, 'disabled', isDisabled);
  }

  private setSelected(value: any) {
    const nativeElement = this._element.nativeElement;
    let targetIndex = 0; // Default to "Pick an option"

    if (value && this._originalItems.length > 0) {
      // Compare against original items since form control stores original values
      const index = this._originalItems.findIndex(item => {
        const itemVal = this.itemValue(item);
        // Handle both object comparison and primitive comparison
        return equals(itemVal, value) || equals(item, value);
      });

      if (index >= 0) {
        // Found the item, select it (+1 because of "Pick an option")
        targetIndex = index + 1;
      }
    }

    // Only update if different from current selection
    if (nativeElement.selectedIndex !== targetIndex) {
      this._renderer.setProperty(nativeElement, 'selectedIndex', targetIndex);
    }
  }

  private itemLabel(item: any) {
    return splitPath(orS(item), this.bindLabel, item);
  }

  private itemValue(item: any) {
    return splitPath(item, this.bindValue, item);
  }

  selectedValue(index: number) {
    // Return value from original items since form control expects original values
    return this.itemValue(nth(index - 1, this._originalItems)); // -1 because of "Pick an option"
  }
}

